<template>
  <Teleport to="body">
    <div
      v-if="open"
      class="profile_scrim fixed inset-0 z-50 pointer-events-none"
      role="presentation"
    >
      <div
        ref="panel"
        class="profile fixed pointer-events-auto w-72 max-w-[90vw] bg-bg-elevated border border-border rounded-lg shadow-lg p-4 text-text-primary"
        :style="panelPosition"
        role="dialog"
        :aria-label="messages.profilePopover.dialogLabel"
        tabindex="-1"
        @keydown.esc.stop="onClose"
      >
        <div class="flex items-center gap-3">
          <img
            v-if="avatarUrl && !avatarFailed"
            :src="avatarUrl"
            :alt="messages.profilePopover.avatarAlt(displayName)"
            class="h-12 w-12 rounded-full border border-border bg-bg-surface"
            referrerpolicy="no-referrer"
            @error="onAvatarError"
          />
          <div
            v-else
            class="h-12 w-12 rounded-full bg-accent/20 border border-accent/40 flex items-center justify-center text-accent text-lg font-semibold"
          >
            {{ avatarInitial }}
          </div>
          <div class="min-w-0">
            <div class="font-semibold text-text-primary truncate">{{ displayName }}</div>
            <div class="text-xs text-text-muted truncate">@{{ username }}</div>
          </div>
        </div>

        <section
          class="mt-4"
          aria-labelledby="profile-popover-identity-heading"
        >
          <h2 id="profile-popover-identity-heading" class="sr-only">
            {{ messages.profilePopover.identity }}
          </h2>
          <dl class="grid grid-cols-[auto_1fr] gap-x-3 gap-y-1 text-xs">
            <dt class="text-text-muted">{{ messages.profilePopover.userId }}</dt>
            <dd class="font-mono text-text-primary tabular-nums">{{ userIdLabel }}</dd>
            <dt class="text-text-muted">{{ messages.profilePopover.discordId }}</dt>
            <dd class="font-mono text-text-primary tabular-nums break-all">{{ discordIdLabel }}</dd>
          </dl>
        </section>

        <section
          v-if="profilePopoverLinks.length > 0"
          class="mt-4"
          aria-labelledby="profile-popover-quick-links-heading"
        >
          <h2 id="profile-popover-quick-links-heading" class="text-[10px] uppercase tracking-wider text-text-secondary font-semibold px-2 mb-1">
            {{ messages.profilePopover.quickLinks }}
          </h2>
          <RouterLink
            v-for="entry in profilePopoverLinks"
            :key="entry.routeName"
            :to="linkTo(entry)"
            class="flex items-center w-full px-2 py-1.5 rounded-md text-sm text-text-primary hover:bg-bg-surface hover:text-accent"
            @click="onClose"
          >
            <AppIcon
              v-if="entry.profilePopover.icon"
              :name="entry.profilePopover.icon"
              :size="16"
              class="mr-2 shrink-0"
            />
            <span>{{ entry.profilePopover.label }}</span>
          </RouterLink>
        </section>

        <section
          v-if="officerFor.length > 0"
          class="mt-4"
          aria-labelledby="profile-popover-officer-heading"
        >
          <h2 id="profile-popover-officer-heading" class="text-xs text-text-muted mb-1">
            {{ messages.profilePopover.officerOf }}
          </h2>
          <ul class="flex flex-col gap-1">
            <li
              v-for="g in officerFor"
              :key="g.id"
              class="text-xs px-2 py-1 rounded-md bg-bg-surface border border-border text-text-primary"
            >
              <div class="font-medium truncate">{{ g.name }}</div>
              <div class="font-mono text-text-muted tabular-nums break-all">{{ g.id }}</div>
            </li>
          </ul>
        </section>
        <p v-else class="mt-4 text-xs text-text-muted">{{ messages.profilePopover.noOfficer }}</p>

        <LanguageSelector />
        <AppButton
          :variant="ButtonVariant.Danger"
          :size="ButtonSize.Sm"
          class="mt-4 w-full"
          @click="onLogout"
        >{{ messages.profilePopover.logout }}</AppButton>
      </div>
    </div>
  </Teleport>
</template>

<script setup lang="ts">
import {
  computed,
  nextTick,
  onBeforeUnmount,
  onMounted,
  ref,
  watch,
} from 'vue'
import type { CSSProperties } from 'vue'
import { RouterLink } from 'vue-router'

import { useDiscordAvatar, useFocusTrap } from '@/shared/composables'
import { useLocaleMessages } from '@/shared/composables/useI18n'
import { defineLocaleCatalog } from '@/shared/i18n'
import { appShellEn } from '@/app/layout/locales/en.ts'
import { appShellRu } from '@/app/layout/locales/ru.ts'
import { AppButton, AppIcon, ButtonSize, ButtonVariant } from '@/shared/ui'
import LanguageSelector from '@/shared/ui/LanguageSelector.vue'
import { getProfilePopoverLinks, type ProfilePopoverLink } from '@/app/nav'
import type { SessionGuild, SessionUser } from '@/app/session'
import { clampPanelPosition } from './lib/profilePopoverPosition.ts'

const PROFILE_TRIGGER_SELECTOR = 'header [aria-haspopup="dialog"]'

/**
 * Mirror of the `$space-2` SCSS variable / `--space-2` CSS custom property
 * (0.5rem = 8px). The popover keeps this same distance from the viewport
 * edge after horizontal clamping so the safe margin stays consistent with
 * the rest of the app's spacing tokens.
 */
const PANEL_SAFE_MARGIN_PX = 8

export interface ProfilePopoverProps {
  readonly open: boolean
  readonly session: SessionUser | null
  readonly onClose: () => void
  readonly onLogout: () => void
}

const props = defineProps<ProfilePopoverProps>()
const messages = useLocaleMessages(defineLocaleCatalog({ en: appShellEn, ru: appShellRu }))

const panel = ref<HTMLDivElement | null>(null)
const panelPosition = ref<Readonly<CSSProperties>>({})
const { activate, deactivate } = useFocusTrap(panel)

const discordIdLabel = computed<string>(() =>
  props.session ? props.session.discordUserId : '—',
)

const userIdLabel = computed<string>(() => {
  if (!props.session || props.session.userId === '0') return '—'
  return props.session.userId
})

const username = computed<string>(() => props.session?.username ?? '')

const officerFor = computed<ReadonlyArray<SessionGuild>>(
  () => props.session?.officerFor ?? [],
)

const { avatarUrl, avatarFailed, onAvatarError, displayName, avatarInitial } =
  useDiscordAvatar({ size: 128 })

const profilePopoverLinks = computed<ReadonlyArray<ProfilePopoverLink>>(() =>
  getProfilePopoverLinks().filter((entry) => entry.visible.value),
)

function onClose(): void {
  props.onClose()
}

function onLogout(): void {
  props.onLogout()
}

/**
 * Resolve a profile-popover entry's `RouterLink :to` target. Routes with
 * params carry a live `params` computed; routes without just get `{ name }`.
 * The `params` computed returns `undefined` while the descriptor is
 * invisible, which keeps a brief race window from producing a malformed link.
 */
function linkTo(
  entry: ProfilePopoverLink,
): { name: string; params?: Readonly<Record<string, string>> } {
  const params = entry.params?.value
  return params !== undefined ? { name: entry.routeName, params } : { name: entry.routeName }
}

function updatePanelPosition(): void {
  if (!props.open) return

  const trigger = document.querySelector<HTMLElement>(PROFILE_TRIGGER_SELECTOR)
  if (!trigger) return

  const triggerRect = trigger.getBoundingClientRect()
  // The panel ref may not be populated yet on the first invocation (the
  // teleport target mounts after `props.open` flips). Fall back to the
  // configured `w-72` width (288px) so the clamp still has a usable
  // measurement before the panel is in the DOM.
  const panelEl = panel.value
  const panelRect = panelEl?.getBoundingClientRect()
  const panelWidth = panelRect && panelRect.width > 0 ? panelRect.width : 288
  const panelHeight = panelRect && panelRect.height > 0 ? panelRect.height : 0

  const { left, top } = clampPanelPosition(
    { right: triggerRect.right, bottom: triggerRect.bottom },
    { width: panelWidth, height: panelHeight },
    { width: window.innerWidth },
    PANEL_SAFE_MARGIN_PX,
  )

  panelPosition.value = {
    top: `${top}px`,
    left: `${left}px`,
  }
}

/**
 * Vuestic teleports AppSelect's option list to `<body>`, so the selected
 * option is not a DOM descendant of the profile panel. Treat that listbox as
 * part of the panel when its `id` is referenced by one of the panel's
 * `aria-controls` attributes; otherwise the document mousedown handler closes
 * and unmounts the popover before the option's click can update the locale.
 */
function isPanelControlledListboxTarget(root: HTMLElement, target: Node): boolean {
  if (!(target instanceof Element)) return false

  const listbox = target.closest<HTMLElement>('[role="listbox"][id]')
  if (listbox === null) return false

  for (const control of root.querySelectorAll<HTMLElement>('[aria-controls]')) {
    if (control.getAttribute('aria-controls') === listbox.id) return true
  }
  return false
}

// Close on mousedown outside the panel. Listen at the document level so the
// handler works even when the click target is the (non-interactive) scrim.
function onDocMouseDown(event: MouseEvent): void {
  if (!props.open) return
  const root = panel.value
  const target = event.target
  if (
    !root
    || !(target instanceof Node)
    || root.contains(target)
    || isPanelControlledListboxTarget(root, target)
  ) return
  props.onClose()
}

watch(
  () => props.open,
  async (isOpen) => {
    if (isOpen) {
      await nextTick()
      updatePanelPosition()
      activate()
    } else {
      deactivate()
    }
  },
)

onMounted(() => {
  document.addEventListener('mousedown', onDocMouseDown)
  window.addEventListener('resize', updatePanelPosition)
  window.addEventListener('scroll', updatePanelPosition, true)
})

onBeforeUnmount(() => {
  document.removeEventListener('mousedown', onDocMouseDown)
  window.removeEventListener('resize', updatePanelPosition)
  window.removeEventListener('scroll', updatePanelPosition, true)
  deactivate()
})
</script>
